import { useEffect, useState } from 'react';
import { legalLinks, type ILegalLinks } from '@featit/shared/legal';
import { readIdentity } from '@/features/app-setup/appSetup';

/**
 * F-0554, AC-TA7: the website's legal pages, from the deployment's own `/.well-known/featit`
 * (`websiteOrigin`). One read per page load, shared by every screen that links them; a failed read
 * is forgotten, so the next screen asks again.
 */
let pending: Promise<ILegalLinks | null> | null = null;

export function loadLegalLinks(): Promise<ILegalLinks | null> {
  pending ??= readIdentity()
    .then((identity) => legalLinks(identity.websiteOrigin))
    .catch(() => {
      pending = null;
      return null;
    });
  return pending;
}

/** For the specs: forget the shared read. */
export function forgetLegalLinks(): void {
  pending = null;
}

/**
 * The three addresses; `null` where the deployment names no website or cannot be asked, and
 * `undefined` while it has not answered yet.
 */
export function useLegalLinks(): ILegalLinks | null | undefined {
  return useLegalLinksWithRetry().links;
}

/** F-0604: the links and a way to read them again, for a placeholder's Try again. */
export function useLegalLinksWithRetry(): {
  links: ILegalLinks | null | undefined;
  retry: () => void;
} {
  const [links, setLinks] = useState<ILegalLinks | null | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    void loadLegalLinks().then((loaded) => {
      if (live) setLinks(loaded);
    });
    return () => {
      live = false;
    };
  }, [attempt]);
  return { links, retry: () => setAttempt((count) => count + 1) };
}
